import { BaseLayout } from '@/components/layout/BaseLayout'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { Card, CardContent, CardFooter, CardTitle } from '@/components/common/Card'
import { ProductForm } from '@/components/products/ProductForm'
import { ProductPreview } from '@/components/products/ProductPreview'
import { useStore } from '@/features/stores/StoreContext'
import { supabase } from '@/lib/supabase/client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Plus, Pencil, Trash2, Package, Minus, Grid3x3, List } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface Product {
  id: string
  name: string
  description: string
  price: number
  compare_at_price: number | null
  cost_price: number | null
  sku: string | null
  brand: string | null
  stock: number
  category_id: string | null
  images: string[]
  tags: string[]
  is_featured: boolean
  status: string
  created_at: string
}

interface Category {
  id: string
  name: string
  parent_id: string | null
}

export function ProductsPage() {
  const { currentStore } = useStore()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [previewData, setPreviewData] = useState<any>(null)
  const [minimizedProducts, setMinimizedProducts] = useState<Array<{ id: string; name: string; data: any }>>([])
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products', currentStore?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*, categories(name)')
        .eq('store_id', currentStore?.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as (Product & { categories: { name: string } | null })[]
    },
    enabled: !!currentStore?.id,
  })

  const { data: categories = [] } = useQuery({
    queryKey: ['categories', currentStore?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, parent_id')
        .eq('store_id', currentStore?.id)
        .order('name')
      if (error) throw error
      return data as Category[]
    },
    enabled: !!currentStore?.id,
  })

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const slug = data.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Date.now()
      const { error } = await supabase.from('products').insert({
        store_id: currentStore?.id,
        name: data.name,
        slug,
        description: data.description,
        price: parseFloat(data.price) || 0,
        compare_at_price: data.compare_at_price ? parseFloat(data.compare_at_price) : null,
        cost_price: data.cost_price ? parseFloat(data.cost_price) : null,
        sku: data.sku || null,
        brand: data.brand || null,
        stock: parseInt(data.stock) || 0,
        category_id: data.category_id || null,
        images: data.images || [],
        tags: data.tags || [],
        is_featured: data.is_featured || false,
        status: data.status || 'draft',
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Product created successfully')
      setOpen(false)
    },
    onError: () => toast.error('Failed to create product'),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const { error } = await supabase
        .from('products')
        .update({
          name: data.name,
          description: data.description,
          price: parseFloat(data.price),
          compare_at_price: data.compare_at_price ? parseFloat(data.compare_at_price) : null,
          cost_price: data.cost_price ? parseFloat(data.cost_price) : null,
          sku: data.sku || null,
          brand: data.brand || null,
          stock: parseInt(data.stock) || 0,
          category_id: data.category_id || null,
          images: data.images,
          tags: data.tags,
          is_featured: data.is_featured,
        })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Product updated successfully')
      setOpen(false)
      setEditingProduct(null)
    },
    onError: () => toast.error('Failed to update product'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('products').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Product deleted successfully')
    },
    onError: () => toast.error('Failed to delete product'),
  })

  const handleSubmit = (data: any) => {
    if (!data.name || !data.price || !data.category_id) {
      toast.error('Please fill all required fields (Name, Price, Category)')
      return
    }
    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, data: { ...data, status: 'active' } })
    } else {
      createMutation.mutate({ ...data, status: 'active' })
    }
  }

  const handleSaveDraft = async (data: any) => {
    try {
      if (!data.name) {
        toast.error('Product name is required')
        return false
      }
      if (editingProduct) {
        await updateMutation.mutateAsync({ id: editingProduct.id, data: { ...data, status: 'draft' } })
      } else {
        await createMutation.mutateAsync({ ...data, status: 'draft', price: data.price || 0 })
      }
      toast.success('Draft saved')
      return true
    } catch (error) {
      console.error('Save draft error:', error)
      toast.error('Failed to save draft')
      return false
    }
  }

  const handleEdit = (product: Product) => {
    setEditingProduct(product)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setEditingProduct(null)
    setPreviewData(null)
  }

  const handleMinimize = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    const productData = previewData || editingProduct
    if (productData) {
      const id = editingProduct?.id || `new-${Date.now()}`
      const name = productData.name || 'New Product'
      setMinimizedProducts([...minimizedProducts, { id, name, data: productData }])
    }
    setOpen(false)
    setEditingProduct(null)
    setPreviewData(null)
  }

  const handleRestore = (id: string) => {
    const product = minimizedProducts.find(p => p.id === id)
    if (product) {
      if (id.startsWith('new-')) {
        setEditingProduct(null)
        setPreviewData(product.data)
      } else {
        setEditingProduct(product.data as Product)
      }
      setMinimizedProducts(minimizedProducts.filter(p => p.id !== id))
      setOpen(true)
    }
  }

  const handleRemoveMinimized = (id: string) => {
    setMinimizedProducts(minimizedProducts.filter(p => p.id !== id))
  }

  return (
    <BaseLayout>
      <div className="p-8">
        <PageHeader
          title="Products"
          description="Manage your product catalog"
          action={
            <div className="flex gap-2">
              <div className="flex border rounded-lg">
                <Button
                  size="sm"
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  onClick={() => setViewMode('grid')}
                  className="rounded-r-none"
                >
                  <Grid3x3 className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  onClick={() => setViewMode('list')}
                  className="rounded-l-none"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Button onClick={() => { setEditingProduct(null); setOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </div>
          }
        />

        {isLoading ? (
          <LoadingSpinner />
        ) : products.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No products yet"
            description="Get started by creating your first product to build your catalog"
            action={{ label: 'Add Product', onClick: () => setOpen(true) }}
          />
        ) : viewMode === 'grid' ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((product) => (
              <Card key={product.id} hover>
                {product.images?.[0] && (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-48 object-cover rounded-t-lg -mt-4 -mx-4 mb-3"
                  />
                )}
                <CardTitle className="line-clamp-1">{product.name}</CardTitle>
                <CardContent className="mt-2">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {product.description || 'No description'}
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      {product.categories?.name && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          {product.categories.name}
                        </span>
                      )}
                      {product.brand && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                          {product.brand}
                        </span>
                      )}
                    </div>
                    {product.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {product.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="text-xs px-2 py-0.5 bg-muted rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {product.sku && (
                      <p className="text-xs text-muted-foreground">SKU: {product.sku}</p>
                    )}
                  </div>
                </CardContent>
                <CardFooter>
                  <div>
                    <span className="text-xl font-bold">${product.price.toFixed(2)}</span>
                    {product.compare_at_price && (
                      <span className="text-sm text-muted-foreground line-through ml-2">
                        ${product.compare_at_price.toFixed(2)}
                      </span>
                    )}
                    <p className="text-xs text-muted-foreground">Stock: {product.stock}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(product)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteMutation.mutate(product.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg border">
            <table className="w-full">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Product</th>
                  <th className="text-left p-3 font-medium">Category</th>
                  <th className="text-left p-3 font-medium">Brand</th>
                  <th className="text-left p-3 font-medium">SKU</th>
                  <th className="text-left p-3 font-medium">Stock</th>
                  <th className="text-left p-3 font-medium">Price</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-right p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-b hover:bg-muted/30">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                            <Package className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {product.description || 'No description'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      {product.categories?.name ? (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          {product.categories.name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 text-sm">
                      {product.brand || <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {product.sku || '—'}
                    </td>
                    <td className="p-3">
                      <span className={product.stock > 0 ? 'text-green-600' : 'text-red-600'}>
                        {product.stock}
                      </span>
                    </td>
                    <td className="p-3">
                      <div>
                        <span className="font-semibold">${product.price.toFixed(2)}</span>
                        {product.compare_at_price && (
                          <span className="text-sm text-muted-foreground line-through ml-2">
                            ${product.compare_at_price.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        product.status === 'active' ? 'bg-green-100 text-green-700' :
                        product.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {product.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(product)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteMutation.mutate(product.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-6xl h-[95vh] p-0">
            <div className="grid grid-cols-2 h-full">
              {/* Left: Form */}
              <div className="overflow-y-auto p-4 border-r">
                <div className="flex items-center justify-between mb-4">
                  <DialogHeader>
                    <DialogTitle>{editingProduct ? 'Edit Product' : 'Create Product'}</DialogTitle>
                  </DialogHeader>
                  <Button size="sm" variant="outline" onClick={(e) => handleMinimize(e)}>
                    <Minus className="h-4 w-4" />
                  </Button>
                </div>
                <ProductForm
                  initialData={editingProduct ? {
                    name: editingProduct.name,
                    description: editingProduct.description || '',
                    short_description: editingProduct.description?.substring(0, 100) || '',
                    price: editingProduct.price.toString(),
                    compare_at_price: editingProduct.compare_at_price?.toString() || '',
                    cost_price: editingProduct.cost_price?.toString() || '',
                    sku: editingProduct.sku || '',
                    brand: editingProduct.brand || '',
                    stock: editingProduct.stock.toString(),
                    category_id: editingProduct.category_id || '',
                    images: editingProduct.images || [],
                    tags: editingProduct.tags || [],
                    is_featured: editingProduct.is_featured,
                  } : undefined}
                  categories={categories}
                  onSubmit={handleSubmit}
                  onCancel={handleClose}
                  isLoading={createMutation.isPending || updateMutation.isPending}
                  onFormChange={setPreviewData}
                  onSaveDraft={handleSaveDraft}
                />
              </div>

              {/* Right: Preview */}
              <div className="bg-muted/30 p-4 overflow-y-auto">
                <h3 className="font-semibold mb-3 text-sm">Product Preview</h3>
                <ProductPreview product={previewData || editingProduct} />
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Minimized Products Bar */}
        {minimizedProducts.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-2 z-50">
            <div className="flex gap-2 overflow-x-auto">
              {minimizedProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg min-w-[200px] cursor-pointer hover:bg-muted/80"
                  onClick={() => handleRestore(product.id)}
                >
                  <Package className="h-4 w-4" />
                  <span className="text-sm font-medium truncate flex-1">{product.name}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveMinimized(product.id)
                    }}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </BaseLayout>
  )
}
